"use client"

import { useEffect, useState, useTransition } from "react"
import {
  ChassisDetail,
  ComponentItem,
  ValidationResponse,
  validateConfig,
} from "@lib/data/configurator"
import { CPU_SPECS, formatCpuSpecLine } from "@lib/data/cpu-specs"
import { DRIVE_SPECS, formatDriveSpecLine } from "@lib/data/drive-specs"

const STORAGE_CATEGORIES = new Set(["storage_sff", "storage_lff", "storage_nvme_aic"])

type Selection = Record<string, number>

const SECTIONS: Array<{
  id: string
  label: string
  hint: string
  categories: string[]
}> = [
  { id: "cpu", label: "CPU", hint: "One model, qty fixed at 2 (dual-socket).", categories: ["cpu"] },
  { id: "ram", label: "RAM", hint: "DDR4 ECC. 4 modules recommended (one per memory channel, balanced across both sockets). Minimum 2; max = chassis DIMM slot count. All modules must be the same SKU.", categories: ["ram"] },
  { id: "storage", label: "Storage", hint: "Bay-native drives, NVMe AIC (1 per PCIe slot), caddies for SFF-in-LFF.", categories: ["storage_nvme_aic", "storage_sff", "storage_lff", "caddy"] },
  { id: "hba", label: "SAS HBA", hint: "Required for the RH2288H V3 25-bay backplane. Included free.", categories: ["hba"] },
  { id: "psu", label: "PSU", hint: "1 or 2 PSUs. If 2, both must be the same model.", categories: ["psu"] },
  { id: "cooling", label: "Cooling", hint: "Optional quiet fan kit — projected noise level shows live in the build summary based on your drive count.", categories: ["cooling"] },
  { id: "nic", label: "Network", hint: "PCIe NICs use a slot each. FlexLOM is mezzanine, 1U-only. Skip only if you're supplying your own — otherwise the build ships without networking.", categories: ["nic_pcie", "nic_flexlom"] },
  { id: "os", label: "OS / boot", hint: "Choose one OS, a RAID layout, optionally a USB boot drive.", categories: ["os", "raid", "boot_usb"] },
]

const CATEGORY_LABELS: Record<string, string> = {
  cpu: "CPU pair",
  ram: "RAM modules",
  storage_sff: "2.5\" drives",
  storage_lff: "3.5\" drives",
  storage_nvme_aic: "NVMe AIC",
  // Virtual labels used by the storage section split (SSD vs HDD).
  storage_sata_ssd: "SATA SSD",
  storage_sata_hdd: "SATA HDD",
  caddy: "2.5→3.5 caddies",
  psu: "Power supplies",
  nic_pcie: "PCIe NICs",
  nic_flexlom: "FlexLOM (1U)",
  hba: "LSI SAS HBA (included, required)",
  cooling: "Quiet fan kit (optional)",
  os: "Operating system",
  raid: "Boot RAID",
  boot_usb: "USB boot drive",
}

const RADIO_CATEGORIES = new Set(["cpu", "os", "raid"])
/** Categories where only one SKU may be selected at a time, but with a numeric quantity (vs. radio's fixed qty). */
const SINGLE_SKU_QTY_CATEGORIES = new Set(["ram", "psu"])

function fmtPrice(usd: number | null | undefined) {
  if (usd == null) return "—"
  return `$${usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** Sum total RAM (GB) across the build by parsing per-DIMM capacity from the
 *  RAM handle (the catalog uses 'ram-{N}gb-ddr4-...' as the convention). */
function computeTotalRamGb(selection: Selection): number {
  let gb = 0
  for (const [handle, qty] of Object.entries(selection)) {
    if (qty <= 0) continue
    const m = handle.match(/^ram-(\d+)gb-/i)
    if (m) gb += Number(m[1]) * qty
  }
  return gb
}

/** Sensible default selection on first load: 2× CPU, 4× RAM (one per memory
 *  channel × socket = balanced baseline), 1 PSU, and the bundled HBA on the
 *  25-bay chassis. Storage is intentionally LEFT EMPTY so the customer always
 *  picks their own drive mix — the OS picker stays gated to 'os-none' until
 *  at least one drive is added. */
function computeDefaultSelection(detail: ChassisDetail): Selection {
  const sel: Selection = {}
  const first = (cat: string) => detail.components[cat]?.[0]?.handle
  const cpu = first("cpu");           if (cpu)  sel[cpu]  = 2
  const ram = first("ram");           if (ram)  sel[ram]  = 4
  const psu = first("psu");           if (psu)  sel[psu]  = 1
  // HBA: only present for chassis that require it (25-bay SFF). Preselect
  // so the build is valid out-of-the-box — the cost is bundled at \$0 anyway.
  const hba = first("hba");           if (hba)  sel[hba]  = 1
  const os = first("os");             if (os)   sel[os]   = 1
  return sel
}

export default function Configurator({
  detail,
  onSubmit,
}: {
  detail: ChassisDetail
  onSubmit: (components: Array<{ handle: string; quantity: number }>) => Promise<void>
}) {
  const [selection, setSelection] = useState<Selection>(() => computeDefaultSelection(detail))
  const [validation, setValidation] = useState<ValidationResponse | null>(null)
  const [isPending, startTransition] = useTransition()

  useEffect(() => {
    // Always run /validate, even when no components are selected — that path
    // returns the chassis price as a single line item so the customer sees a
    // running total instead of $0 from the moment the page loads.
    const components = Object.entries(selection)
      .filter(([, q]) => q > 0)
      .map(([handle, quantity]) => ({ handle, quantity }))
    const t = setTimeout(async () => {
      try {
        const r = await validateConfig({ chassis: detail.productHandle, components })
        setValidation(r)
      } catch (e) {
        // Don't leave the user staring at a stale total if /validate breaks.
        // eslint-disable-next-line no-console
        console.error("[configurator] validate failed:", e)
      }
    }, 250)
    return () => clearTimeout(t)
  }, [selection, detail.productHandle])

  const setRadio = (handles: string[], pickedHandle: string, qty: number) => {
    setSelection((s) => {
      const next = { ...s }
      for (const h of handles) if (h !== pickedHandle) next[h] = 0
      next[pickedHandle] = qty
      return next
    })
  }
  const setQty = (handle: string, qty: number) =>
    setSelection((s) => ({ ...s, [handle]: Math.max(0, qty) }))

  const handleSubmit = () => {
    const items = Object.entries(selection)
      .filter(([, q]) => q > 0)
      .map(([handle, quantity]) => ({ handle, quantity }))
    startTransition(() => onSubmit(items))
  }

  const errors = !validation?.valid ? validation?.errors ?? [] : []
  const warnings = validation?.warnings ?? []
  const total = validation?.pricing.total ?? 0

  // Count selected drives. USB boot sticks are only for NVMe-boot chaining,
  // not OS install targets, so they don't count here. Used to disable
  // non-"os-none" OS choices when the build has no drive to install onto.
  const driveCount = (["storage_sff", "storage_lff", "storage_nvme_aic"] as const)
    .flatMap((cat) => detail.components[cat] ?? [])
    .reduce((sum, item) => sum + (selection[item.handle] ?? 0), 0)
  const osBlocked = driveCount === 0

  // PCIe slot accounting. NVMe AIC, PCIe NICs, and the LSI HBA each take
  // exactly one general-purpose slot. FlexLOM is mezzanine and doesn't count.
  // Cap the customer's +button at the slot budget to prevent over-allocation.
  const PCIE_SLOT_CATS = new Set(["storage_nvme_aic", "nic_pcie", "hba"])
  const pcieSlotsUsed = (Object.keys(detail.components) as Array<keyof typeof detail.components>)
    .filter((c) => PCIE_SLOT_CATS.has(String(c)))
    .flatMap((cat) => detail.components[cat] ?? [])
    .reduce((sum, item) => sum + (selection[item.handle] ?? 0), 0)
  const pcieSlotsTotal = detail.pcieSlots
  const pcieSlotsRemaining = Math.max(0, pcieSlotsTotal - pcieSlotsUsed)

  // If storage drops to zero with a non-"os-none" OS selected, snap the
  // selection back to "os-none" so the build stays consistent with what
  // the picker now allows.
  useEffect(() => {
    if (!osBlocked) return
    const osHandles = (detail.components.os ?? []).map((i) => i.handle)
    const pickedRealOs = osHandles.find((h) => h !== "os-none" && (selection[h] ?? 0) > 0)
    if (pickedRealOs) {
      setSelection((s) => {
        const next = { ...s }
        for (const h of osHandles) next[h] = h === "os-none" ? 1 : 0
        return next
      })
    }
  }, [osBlocked, detail.components.os, selection])

  return (
    <div className="cfg" style={{ maxWidth: 1080, margin: "0 auto", padding: "20px 16px" }}>
      <header style={{ marginBottom: 8 }}>
        <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{detail.title}</h1>
        <div className="cfg-spec-bar">
          <span><strong>{detail.formFactor}</strong> chassis</span>
          <span><strong>{detail.sffBays > 0 ? `${detail.sffBays}` : detail.lffBays}</strong> × {detail.sffBays > 0 ? "2.5\"" : "3.5\""} bays</span>
          <span><strong>{detail.dimmSlots}</strong> DIMM slots</span>
          <span><strong>{detail.pcieSlots}</strong> PCIe slots{detail.hasFlexLom ? " + FlexLOM" : ""}</span>
          <span>max <strong>{detail.maxTdpPerSocket}W</strong> TDP/socket</span>
        </div>
      </header>

      <div className="cfg-shell">
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {SECTIONS.map((section) => {
            // Storage section is special-cased so the SFF bucket is split
            // by SKU prefix into a SATA-SSD block above a SATA-HDD block,
            // and NVMe AIC sits after the spinning-disk options. Order:
            //   SATA SSD → SATA HDD → NVMe AIC → caddies.
            const renderableCats: Array<{ cat: string; items: ComponentItem[] }> =
              section.id === "storage"
                ? (() => {
                    const sff = detail.components.storage_sff ?? []
                    const lff = detail.components.storage_lff ?? []
                    const nvme = detail.components.storage_nvme_aic ?? []
                    const caddy = detail.components.caddy ?? []
                    const ssd = sff.filter((i) => i.handle.startsWith("ssd-"))
                    const hdd = [
                      ...sff.filter((i) => i.handle.startsWith("hdd-")),
                      ...lff,
                    ]
                    const out: Array<{ cat: string; items: ComponentItem[] }> = []
                    if (ssd.length) out.push({ cat: "storage_sata_ssd", items: ssd })
                    if (hdd.length) out.push({ cat: "storage_sata_hdd", items: hdd })
                    if (nvme.length) out.push({ cat: "storage_nvme_aic", items: nvme })
                    if (caddy.length) out.push({ cat: "caddy", items: caddy })
                    return out
                  })()
                : section.categories
                    .filter((c) => detail.components[c])
                    .map((c) => ({ cat: c, items: detail.components[c] ?? [] }))
            if (renderableCats.length === 0) return null
            return (
              <section key={section.id} className="cfg-section">
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
                  <h3 style={{ fontSize: 13, fontWeight: 600, margin: 0, color: "#111827" }}>{section.label}</h3>
                  <span className="cfg-section-hint" style={{ margin: 0 }}>{section.hint}</span>
                </div>
                {renderableCats.map(({ cat, items }) => {
                  // OS install requires a target (drive or USB boot). Disable
                  // every non-"os-none" choice when the build has zero targets.
                  const disabledHandles =
                    cat === "os" && osBlocked
                      ? new Set((detail.components.os ?? []).filter((i) => i.handle !== "os-none").map((i) => i.handle))
                      : undefined
                  // PCIe-consuming categories share the chassis slot budget.
                  // When the budget is exhausted the renderer disables the +
                  // button so the customer can't over-allocate.
                  const slotConsumer = PCIE_SLOT_CATS.has(cat)
                  return (
                    <CategoryBlock
                      key={cat}
                      category={cat}
                      items={items}
                      selection={selection}
                      disabledHandles={disabledHandles}
                      disabledNote={cat === "os" && osBlocked ? "Add a drive first — USB boot sticks chain to NVMe but aren't an OS install target." : undefined}
                      pcieRemaining={slotConsumer ? pcieSlotsRemaining : undefined}
                      onQtyChange={(handle, qty) => {
                        // Slot cap: refuse increases when no slots remain.
                        // Decreases (qty < current) are always allowed.
                        if (slotConsumer) {
                          const current = selection[handle] ?? 0
                          if (qty > current && pcieSlotsRemaining < qty - current) {
                            return
                          }
                        }
                        if (RADIO_CATEGORIES.has(cat)) {
                          const handles = items.map((i) => i.handle)
                          setRadio(handles, handle, qty)
                        } else if (SINGLE_SKU_QTY_CATEGORIES.has(cat)) {
                          // Pick one SKU + free quantity. Setting a positive qty
                          // on one zeros out the others in this category.
                          const handles = items.map((i) => i.handle)
                          if (qty > 0) {
                            setRadio(handles, handle, qty)
                          } else {
                            setQty(handle, 0)
                          }
                        } else {
                          setQty(handle, qty)
                        }
                      }}
                      isRadio={RADIO_CATEGORIES.has(cat)}
                      cpuPair={cat === "cpu"}
                    />
                  )
                })}
              </section>
            )
          })}
        </div>

        <aside>
          <div className="cfg-summary">
            <h3>Build summary</h3>
            {validation?.pricing?.lineItems?.length ? (
              <ul>
                {validation.pricing.lineItems.map((li) => (
                  <li key={li.handle}>
                    <span title={li.title}>{li.quantity}× {li.title.length > 38 ? li.title.slice(0, 35) + "…" : li.title}</span>
                    <span>{fmtPrice(li.subtotal)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ fontSize: 12, color: "#6b7280", padding: "6px 0 12px" }}>Pick components to see your build.</div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#475569", padding: "6px 0", borderTop: "1px dashed #e5e7eb", marginTop: 4 }}>
              <span>PCIe slots</span>
              <span style={{ fontWeight: 600, color: pcieSlotsRemaining === 0 ? "#b45309" : "#002035" }}>
                {pcieSlotsUsed} / {pcieSlotsTotal}
                {pcieSlotsRemaining === 0 ? " (full)" : ""}
              </span>
            </div>
            {(() => {
              const totalRamGb = computeTotalRamGb(selection)
              if (totalRamGb === 0) return null
              return (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#475569", padding: "6px 0", borderTop: "1px dashed #e5e7eb" }}>
                  <span>Total RAM</span>
                  <span style={{ fontWeight: 600, color: "#002035" }}>{totalRamGb} GB</span>
                </div>
              )
            })()}
            <div className="total">
              <span>Total</span>
              <span className="price">{fmtPrice(total)}</span>
            </div>

            {errors.length > 0 && (
              <div className="cfg-alert error">
                <strong>Fix before checkout</strong>
                <ul>{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
              </div>
            )}
            {warnings.length > 0 && (
              <div className="cfg-alert warn">
                <strong>Heads up</strong>
                <ul>{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
              </div>
            )}

            <div className="cfg-leadtime">
              <strong>Lead time: 5–7 business days.</strong> Each server is assembled from sourced parts, burned in, and tested before handoff. <strong>Servers are pickup at our Dallas (75218) facility or $75 flat insured courier to any DFW-metro data center</strong> — we don't UPS-ship servers.
            </div>
            <div className="cfg-leadtime" style={{ marginTop: 6 }}>
              <strong>Larger quantities may re-price.</strong> Components are sourced from refurb wholesale supply. If you order multiple servers and supply at the displayed price isn't deep enough to fill your order, we'll show you the adjusted unit price at checkout before charging. You can confirm the new price or remove items.
            </div>

            <button
              type="button"
              className="cfg-cta"
              disabled={!validation?.valid || isPending}
              onClick={handleSubmit}
            >
              {isPending ? "Adding…" : validation?.valid ? `Add to cart — ${fmtPrice(total)}` : "Configure to continue"}
            </button>
          </div>
        </aside>
      </div>
    </div>
  )
}

function CategoryBlock({
  category,
  items,
  selection,
  onQtyChange,
  isRadio,
  cpuPair,
  disabledHandles,
  disabledNote,
  pcieRemaining,
}: {
  category: string
  items: ComponentItem[]
  selection: Selection
  onQtyChange: (handle: string, qty: number) => void
  isRadio: boolean
  cpuPair: boolean
  disabledHandles?: Set<string>
  disabledNote?: string
  /** When set, the category competes for PCIe slots. The renderer disables
   *  rows whose current qty is 0 (no slots left to add a new card) and caps
   *  the qty input's `max` so the customer can't type past capacity. */
  pcieRemaining?: number
}) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div className="cfg-cat-label">
        {CATEGORY_LABELS[category] ?? category}
        {pcieRemaining !== undefined && pcieRemaining === 0 && (
          <span style={{ marginLeft: 8, fontFamily: "ui-monospace, SF Mono, Menlo, monospace", fontSize: 10, color: "#b45309", textTransform: "uppercase", letterSpacing: "0.1em" }}>
            PCIe slots full
          </span>
        )}
      </div>
      {disabledNote && (
        <div style={{ fontFamily: "ui-monospace, SF Mono, Menlo, monospace", fontSize: 11, color: "#b45309", padding: "4px 6px", marginBottom: 4 }}>
          ⓘ {disabledNote}
        </div>
      )}
      {items.map((it) => {
        const qty = selection[it.handle] ?? 0
        const selected = qty > 0
        const slotBlocked = pcieRemaining !== undefined && pcieRemaining === 0 && qty === 0
        const disabled = (disabledHandles?.has(it.handle) ?? false) || slotBlocked
        return (
          <label
            key={it.handle}
            className={`cfg-row${selected ? " selected" : ""}`}
            style={disabled ? { opacity: 0.45, cursor: "not-allowed" } : undefined}
          >
            <span>
              {isRadio ? (
                <input
                  type="radio"
                  name={category}
                  checked={qty === (cpuPair ? 2 : 1)}
                  disabled={disabled}
                  onChange={() => onQtyChange(it.handle, cpuPair ? 2 : 1)}
                />
              ) : (
                <input
                  type="checkbox"
                  checked={qty > 0}
                  disabled={disabled}
                  onChange={(e) => onQtyChange(it.handle, e.target.checked ? Math.max(qty, 1) : 0)}
                />
              )}
            </span>
            <span>
              {it.sku && (it.sku.includes("AMZ") || it.sku.endsWith("-NEW")) && (
                <span
                  style={{
                    display: "inline-block",
                    fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.15em",
                    color: "#002035",
                    background: "#5eead4",
                    padding: "2px 6px",
                    borderRadius: 3,
                    textTransform: "uppercase",
                    marginRight: 6,
                    verticalAlign: "middle",
                  }}
                >
                  New
                </span>
              )}
              <span className="title">{it.title}</span>
              {it.sku && <span className="sku" style={{ marginLeft: 8 }}>{it.sku}</span>}
              {category === "cpu" && CPU_SPECS[it.handle] && (
                <>
                  <div
                    style={{
                      fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
                      fontSize: 11,
                      color: "#475569",
                      marginTop: 2,
                    }}
                  >
                    {formatCpuSpecLine(CPU_SPECS[it.handle])}
                  </div>
                  {CPU_SPECS[it.handle].note && (
                    <div
                      style={{
                        fontSize: 11,
                        color: "#002035",
                        fontWeight: 700,
                        marginTop: 2,
                      }}
                    >
                      {CPU_SPECS[it.handle].note}
                    </div>
                  )}
                </>
              )}
              {STORAGE_CATEGORIES.has(category) && DRIVE_SPECS[it.handle] && (
                <div
                  style={{
                    fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
                    fontSize: 11,
                    color: "#475569",
                    marginTop: 2,
                  }}
                >
                  {formatDriveSpecLine(DRIVE_SPECS[it.handle])}
                </div>
              )}
            </span>
            <span className="price">{fmtPrice(it.price)}</span>
            <span className="qty">
              {!isRadio && (
                <input
                  type="number"
                  min={0}
                  max={pcieRemaining !== undefined ? qty + pcieRemaining : undefined}
                  value={qty}
                  onChange={(e) => onQtyChange(it.handle, Number(e.target.value))}
                />
              )}
            </span>
          </label>
        )
      })}
    </div>
  )
}
